import { useEffect, useRef, useState, type ReactElement } from 'react';

export interface MermaidRuntimeModule {
  initialize(options: Record<string, unknown>): void;
  render(id: string, source: string, container?: Element): Promise<{ svg: string }>;
}

export type MermaidRuntimeLoader = () => Promise<MermaidRuntimeModule>;

const defaultLoader: MermaidRuntimeLoader = async () => (await import('mermaid')).default;
const renderQueues = new WeakMap<object, Promise<void>>();
let renderSequence = 0;

/** Mermaid configuration is module-global. Queue initialize+render as one critical section so a
 * deck diagram and a type-coloured B++ diagram cannot overwrite each other's theme mid-render. */
function renderWithTheme(
  mermaid: MermaidRuntimeModule,
  id: string,
  source: string,
  sandbox: Element,
  themeVariables: Record<string, string> | undefined,
): Promise<{ svg: string }> {
  const key = mermaid as object;
  const previous = renderQueues.get(key) ?? Promise.resolve();
  const result = previous.catch(() => undefined).then(async () => {
    mermaid.initialize({ startOnLoad: false, theme: 'base', themeVariables });
    return mermaid.render(id, source, sandbox);
  });
  renderQueues.set(key, result.then(() => undefined, () => undefined));
  return result;
}

/** Starts the lazy chunk fetch at a route boundary instead of on the first audience-visible graph. */
export async function preloadMermaidRuntime(load: MermaidRuntimeLoader = defaultLoader): Promise<void> {
  await load();
}

/** Probe the node Mermaid actually measures. Browser APIs put text metrics on graphics/text nodes,
 * not reliably on `SVGElement.prototype`. */
export function canLayOutSvgText(): boolean {
  if (typeof document === 'undefined' || typeof document.createElementNS !== 'function') return false;
  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  return (
    typeof (text as SVGGraphicsElement).getBBox === 'function' ||
    typeof (text as SVGTextContentElement).getComputedTextLength === 'function'
  );
}

export interface MermaidRuntimeProps {
  source: string;
  ariaLabel: string;
  className?: string;
  testId?: string;
  themeVariables?: Record<string, string>;
  load?: MermaidRuntimeLoader;
  decorateSvg?: (svg: SVGSVGElement) => void;
  onRendered?: () => void;
}

export function MermaidRuntime({
  source,
  ariaLabel,
  className,
  testId,
  themeVariables,
  load = defaultLoader,
  decorateSvg,
  onRendered,
}: MermaidRuntimeProps): ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(() => !canLayOutSvgText());

  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    if (!canLayOutSvgText()) {
      setFailed(true);
      return;
    }
    setFailed(false);
    host.replaceChildren();
    let cancelled = false;
    const sandbox = document.createElement('div');
    sandbox.dataset.mermaidSandbox = 'true';
    sandbox.setAttribute('aria-hidden', 'true');
    Object.assign(sandbox.style, { position: 'absolute', left: '-99999px', top: '0' });
    document.body.appendChild(sandbox);

    void (async () => {
      try {
        const mermaid = await load();
        const id = `waggle-mermaid-${++renderSequence}`;
        const { svg } = await renderWithTheme(mermaid, id, source, sandbox, themeVariables);
        if (cancelled) return;
        host.innerHTML = svg;
        const renderedSvg = host.querySelector('svg');
        renderedSvg?.setAttribute('aria-label', ariaLabel);
        if (renderedSvg instanceof SVGSVGElement) decorateSvg?.(renderedSvg);
        onRendered?.();
      } catch (error) {
        if (cancelled) return;
        console.error('Unable to render Mermaid diagram', error);
        setFailed(true);
      } finally {
        sandbox.remove();
      }
    })();

    return () => {
      cancelled = true;
      sandbox.remove();
    };
  }, [ariaLabel, decorateSvg, load, onRendered, source, themeVariables]);

  return (
    <div ref={hostRef} aria-label={ariaLabel} data-testid={testId} className={className}>
      {failed ? 'Diagram unavailable' : undefined}
    </div>
  );
}
